import React, { useState, useEffect } from 'react';
import axios from 'axios';

// 1. RECEIVE currentUser AS A PROP HERE
const ThesisUpload = ({ currentUser }) => { 
    const [formData, setFormData] = useState({
        title: '',
        abstract: '',
        year: '',
        department: '',
        degree: '',
        major: ''
    });
    const [authors, setAuthors] = useState([{ firstName: '', lastName: '', email: '' }]);
    const [keywords, setKeywords] = useState([]);
    const [file, setFile] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [degreesForDept, setDegreesForDept] = useState([]);
    const [majorsForDegree, setMajorsForDegree] = useState([]);
    const [showDeptModal, setShowDeptModal] = useState(false);
    const [isEditingDept, setIsEditingDept] = useState(false);
    const [editingDeptId, setEditingDeptId] = useState(null);
    const [deptFormData, setDeptFormData] = useState({ name: '', description: '' });
    const [deptDegrees, setDeptDegrees] = useState([]);
    const [newDegreeInput, setNewDegreeInput] = useState('');
    const [newMajorInputs, setNewMajorInputs] = useState({});
    const [inlineDegreeEdit, setInlineDegreeEdit] = useState({ index: null, value: '' });
    const [inlineMajorEdit, setInlineMajorEdit] = useState({ degreeIndex: null, majorIndex: null, value: '' });
    const [quickModal, setQuickModal] = useState(null);
    const [resolutionModal, setResolutionModal] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Processing...');
    const [validationResults, setValidationResults] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);

    const isAdmin = currentUser?.role === 'admin';

    const fetchDepartments = async () => {
        try {
            const res = await axios.get('/api/departments');
            setDepartments(res.data);
        } catch (err) { console.error("Could not fetch departments", err); }
    };

    useEffect(() => {
        fetchDepartments();
    }, []);

    // When department changes, fetch degrees for that department
    useEffect(() => {
        const fetchDegrees = async () => {
            if (!formData.department) {
                setDegreesForDept([]);
                setMajorsForDegree([]);
                setFormData(prev => ({ ...prev, degree: '', major: '' }));
                return;
            }

            try {
                const dept = departments.find(d => d.dept_name === formData.department);
                if (dept) {
                    setDegreesForDept(dept.degrees || []);
                    setFormData(prev => {
                        const degreeStillExists = (dept.degrees || []).some(d => d.degree_name === prev.degree);
                        return degreeStillExists ? prev : { ...prev, degree: '', major: '' };
                    });
                    if (!dept.degrees?.length) setMajorsForDegree([]);
                } else {
                    setDegreesForDept([]);
                    setMajorsForDegree([]);
                    setFormData(prev => ({ ...prev, degree: '', major: '' }));
                }
            } catch (err) { 
                console.error("Could not fetch degrees", err);
                setDegreesForDept([]);
                setMajorsForDegree([]);
            }
        };
        fetchDegrees();
    }, [formData.department, departments]);

    // When degree changes, fetch majors for that degree
    useEffect(() => {
        if (!formData.degree) {
            setMajorsForDegree([]);
            setFormData(prev => ({ ...prev, major: '' }));
            return;
        }
        const degree = degreesForDept.find(d => d.degree_name === formData.degree);
        if (degree) {
            setMajorsForDegree(degree.majors || []);
            setFormData(prev => {
                const majorStillExists = (degree.majors || []).some(m => m.major_name === prev.major);
                return majorStillExists ? prev : { ...prev, major: '' };
            });
        } else {
            setMajorsForDegree([]);
            setFormData(prev => ({ ...prev, major: '' }));
        }
    }, [formData.degree, degreesForDept]);

    // Helper: Convert full name string to {firstName, lastName} object
    const parseAuthorName = (fullName) => {
        if (!fullName) return { firstName: '', lastName: '' };
        let cleanName = fullName.trim();
        
        // Check if name uses the new delimiter format (firstName||lastName) - clean any trailing commas
        if (cleanName.includes('||')) {
            let [firstName, lastName] = cleanName.split('||');
            firstName = (firstName || '').trim().replace(/,\s*$/, '');
            lastName = (lastName || '').trim().replace(/,\s*$/, '');
            return { firstName, lastName };
        }
        
        // Check if name is in citation format "LastName, FirstName"
        if (cleanName.includes(',')) {
            const [last, first] = cleanName.split(',').map(s => s.trim());
            return { firstName: (first || '').trim(), lastName: (last || '').trim() };
        }
        
        // Fallback for old format (space-separated): assume last word is last name
        const parts = cleanName.split(/\s+/);
        if (parts.length === 0) return { firstName: '', lastName: '' };
        if (parts.length === 1) return { firstName: '', lastName: parts[0] };
        const lastName = parts[parts.length - 1];
        const firstName = parts.slice(0, -1).join(' ');
        return { firstName, lastName };
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: name === 'title' ? value.toUpperCase() : value });
    };

    const handleFileChange = (e) => { setFile(e.target.files[0]); };

    const normalizeDegreesForForm = (dept) => {
        if (dept?.degrees && dept.degrees.length > 0) {
            return dept.degrees.map(d => ({
                degree_id: d.degree_id,
                degree_name: d.degree_name,
                majors: (d.majors || []).map(m => ({
                    major_id: m.major_id,
                    major_name: m.major_name,
                    is_default: m.is_default
                }))
            }));
        }
        return [];
    };

    const resetDeptModalState = () => {
        setIsEditingDept(false);
        setEditingDeptId(null);
        setDeptFormData({ name: '', description: '' });
        setDeptDegrees([]);
        setNewDegreeInput('');
        setNewMajorInputs({});
        setInlineDegreeEdit({ index: null, value: '' });
        setInlineMajorEdit({ degreeIndex: null, majorIndex: null, value: '' });
    };

    const openAddDeptModal = () => {
        if (!isAdmin) return;
        resetDeptModalState();
        setShowDeptModal(true);
    };

    const openEditDeptModal = (dept) => {
        if (!isAdmin || !dept) return;
        setIsEditingDept(true);
        setEditingDeptId(dept.dept_id);
        setDeptFormData({ name: dept.dept_name, description: dept.description || '' });
        setDeptDegrees(normalizeDegreesForForm(dept));
        setNewDegreeInput('');
        setNewMajorInputs({});
        setInlineDegreeEdit({ index: null, value: '' });
        setInlineMajorEdit({ degreeIndex: null, majorIndex: null, value: '' });
        setShowDeptModal(true);
    };

    const closeDeptModal = () => {
        setShowDeptModal(false);
        resetDeptModalState();
    };

    const handleAddDegreeToForm = () => {
        const name = newDegreeInput.trim();
        if (!name) return;
        if (deptDegrees.some(d => d.degree_name.toLowerCase() === name.toLowerCase())) {
            alert('This degree already exists in this department.');
            return;
        }
        setDeptDegrees(prev => [...prev, { degree_name: name, majors: [], is_default: prev.length === 0 }]);
        setNewDegreeInput('');
    };

    const handleRemoveDegreeFromForm = (index) => {
        const next = deptDegrees.filter((_, i) => i !== index);
        if (next.length > 0 && !next.some(d => d.is_default)) next[0].is_default = true;
        setDeptDegrees(next);
        if (inlineDegreeEdit.index === index) setInlineDegreeEdit({ index: null, value: '' });
        if (inlineMajorEdit.degreeIndex === index) setInlineMajorEdit({ degreeIndex: null, majorIndex: null, value: '' });
    };

    const handleAddMajorToDegree = (degreeIndex) => {
        const input = (newMajorInputs[degreeIndex] || '').trim();
        if (!input) return;
        const degree = deptDegrees[degreeIndex];
        if (!degree) return;
        if ((degree.majors || []).some(m => m.major_name.toLowerCase() === input.toLowerCase())) {
            alert('This major already exists in this degree.');
            return;
        }
        setDeptDegrees(prev => {
            const updated = [...prev];
            const majors = [...(updated[degreeIndex].majors || []), { major_name: input, is_default: (updated[degreeIndex].majors || []).length === 0 }];
            updated[degreeIndex] = { ...updated[degreeIndex], majors };
            return updated;
        });
        setNewMajorInputs(prev => ({ ...prev, [degreeIndex]: '' }));
    };

    const handleRemoveMajorFromDegree = (degreeIndex, majorIndex) => {
        setDeptDegrees(prev => {
            const updated = [...prev];
            const majors = (updated[degreeIndex]?.majors || []).filter((_, i) => i !== majorIndex);
            if (majors.length > 0 && !majors.some(m => m.is_default)) majors[0].is_default = true;
            updated[degreeIndex] = { ...updated[degreeIndex], majors };
            return updated;
        });
        if (inlineMajorEdit.degreeIndex === degreeIndex && inlineMajorEdit.majorIndex === majorIndex) {
            setInlineMajorEdit({ degreeIndex: null, majorIndex: null, value: '' });
        }
    };

    const saveInlineDegreeEdit = (degreeIndex) => {
        const trimmed = String(inlineDegreeEdit.value || '').trim();
        if (!trimmed) {
            alert('Please enter a degree name.');
            return;
        }
        setDeptDegrees(prev => {
            const updated = [...prev];
            if (updated[degreeIndex]) updated[degreeIndex] = { ...updated[degreeIndex], degree_name: trimmed };
            return updated;
        });
        setInlineDegreeEdit({ index: null, value: '' });
    };

    const saveInlineMajorEdit = (degreeIndex, majorIndex) => {
        const trimmed = String(inlineMajorEdit.value || '').trim();
        if (!trimmed) {
            alert('Please enter a major name.');
            return;
        }
        setDeptDegrees(prev => {
            const updated = [...prev];
            const degree = updated[degreeIndex];
            if (degree?.majors?.[majorIndex]) {
                const majors = [...degree.majors];
                majors[majorIndex] = { ...majors[majorIndex], major_name: trimmed };
                updated[degreeIndex] = { ...degree, majors };
            }
            return updated;
        });
        setInlineMajorEdit({ degreeIndex: null, majorIndex: null, value: '' });
    };

    const conflictKey = (conflict) => `${conflict.type}:${conflict.id}`;

    const createResolutionModal = (mode, conflicts, extra = {}) => {
        const selections = {};
        conflicts.forEach(conflict => {
            selections[conflictKey(conflict)] = {
                action: 'delete_theses',
                targetDeptId: '',
                targetDegreeId: '',
                targetMajorId: ''
            };
        });
        setResolutionModal({ mode, conflicts, selections, ...extra });
    };

    const updateResolutionSelection = (conflict, patch) => {
        setResolutionModal(prev => {
            if (!prev) return prev;
            const key = conflictKey(conflict);
            const nextSelection = { ...(prev.selections[key] || {}), ...patch };
            if (Object.prototype.hasOwnProperty.call(patch, 'targetDeptId')) {
                nextSelection.targetDegreeId = '';
                nextSelection.targetMajorId = '';
            }
            if (Object.prototype.hasOwnProperty.call(patch, 'targetDegreeId')) {
                nextSelection.targetMajorId = '';
            }
            return { ...prev, selections: { ...prev.selections, [key]: nextSelection } };
        });
    };

    const getDeptById = (deptId) => departments.find(d => String(d.dept_id) === String(deptId));
    const getDegreesForResolution = (selection = {}) => getDeptById(selection.targetDeptId)?.degrees || [];
    const getMajorsForResolution = (selection = {}) => {
        const degree = getDegreesForResolution(selection).find(d => String(d.degree_id) === String(selection.targetDegreeId));
        return degree?.majors || [];
    };

    const buildResolutionPayloads = () => {
        if (!resolutionModal) return [];
        return resolutionModal.conflicts.map(conflict => {
            const selection = resolutionModal.selections[conflictKey(conflict)] || {};
            return {
                type: conflict.type,
                id: conflict.id,
                action: selection.action,
                targetDeptId: selection.targetDeptId || undefined,
                targetDegreeId: selection.targetDegreeId || undefined,
                targetMajorId: selection.targetMajorId || undefined
            };
        });
    };

    const validateResolutionPayloads = (payloads) => {
        for (const item of payloads) {
            if (item.action === 'move_to') {
                if (item.type === 'department' && !item.targetDeptId) {
                    alert('Please choose a target department.');
                    return false;
                }
                if (item.type === 'degree' && !item.targetDegreeId) {
                    alert('Please choose a target degree.');
                    return false;
                }
                if (item.type === 'major' && !item.targetMajorId) {
                    alert('Please choose a target major.');
                    return false;
                }
            }
        }
        return true;
    };

    const buildDepartmentPayload = (resolutions = []) => ({
        dept_name: deptFormData.name.trim(),
        description: deptFormData.description.trim() || null,
        degrees: deptDegrees.map(d => ({
            degree_id: d.degree_id,
            degree_name: d.degree_name,
            majors: (d.majors || []).map(m => ({
                major_id: m.major_id,
                major_name: m.major_name
            }))
        })),
        resolutions,
        adminId: currentUser?.user_id
    });

    const refreshDepartmentState = async (preferredDepartment = formData.department) => {
        const res = await axios.get('/api/departments');
        setDepartments(res.data);
        const dept = res.data.find(d => d.dept_name === preferredDepartment);
        setDegreesForDept(dept?.degrees || []);
        const degree = dept?.degrees?.find(d => d.degree_name === formData.degree);
        setMajorsForDegree(degree?.majors || []);
        return res.data;
    };

    const handleSaveDepartment = async (resolutions = []) => {
        if (!isAdmin) return;
        if (!deptFormData.name.trim()) {
            alert('Please enter a department name.');
            return;
        }
        const ok = window.confirm(`${isEditingDept ? 'Update' : 'Add'} the "${deptFormData.name.trim()}" department?`);
        if (!ok) return;

        try {
            const payload = buildDepartmentPayload(resolutions);
            if (isEditingDept) {
                await axios.put(`/api/departments/${editingDeptId}`, payload);
            } else {
                await axios.post('/api/departments', payload);
            }
            await refreshDepartmentState(payload.dept_name);
            setFormData(prev => ({ ...prev, department: payload.dept_name, degree: '', major: '' }));
            closeDeptModal();
        } catch (err) {
            if (err.response?.status === 409 && err.response.data?.error === 'DEPARTMENT_SAVE_NEEDS_RESOLUTION') {
                createResolutionModal('save-department', err.response.data.conflicts || []);
                return;
            }
            alert(err.response?.data?.error || 'Failed to save department.');
            console.error(err);
        }
    };

    const submitDepartmentSaveResolution = async () => {
        const payloads = buildResolutionPayloads();
        if (!validateResolutionPayloads(payloads)) return;
        setResolutionModal(null);
        await handleSaveDepartment(payloads);
    };

    const openQuickModal = (type, action) => {
        if (!isAdmin) return;
        const selectedDept = departments.find(d => d.dept_name === formData.department);
        const selectedDegree = degreesForDept.find(d => d.degree_name === formData.degree);
        const selectedMajor = majorsForDegree.find(m => m.major_name === formData.major);

        if (type === 'department' && action === 'add') {
            openAddDeptModal();
            return;
        }
        if (type === 'department' && action === 'edit') {
            if (!selectedDept) return alert('Please select a department first.');
            openEditDeptModal(selectedDept);
            return;
        }
        if (type === 'degree' && !selectedDept) return alert('Please select a department first.');
        if (type === 'major' && !selectedDegree) return alert('Please select a degree first.');
        if (action !== 'add' && type === 'degree' && !selectedDegree) return alert('Please select a degree first.');
        if (action !== 'add' && type === 'major' && !selectedMajor) return alert('Please select a major first.');

        setQuickModal({
            type,
            action,
            value: type === 'degree' ? (selectedDegree?.degree_name || '') : (selectedMajor?.major_name || ''),
            dept: selectedDept,
            degree: selectedDegree,
            major: selectedMajor
        });
    };

    const submitQuickModal = async () => {
        if (!quickModal || !isAdmin) return;
        const value = String(quickModal.value || '').trim();
        if ((quickModal.action === 'add' || quickModal.action === 'edit') && !value) {
            alert(`Please enter a ${quickModal.type} name.`);
            return;
        }

        try {
            if (quickModal.type === 'degree') {
                if (quickModal.action === 'add') {
                    await axios.post(`/api/departments/${quickModal.dept.dept_id}/degrees`, {
                        degree_name: value,
                        adminId: currentUser.user_id
                    });
                } else if (quickModal.action === 'edit') {
                    if (value === quickModal.degree.degree_name) {
                        setQuickModal(null);
                        return;
                    }
                    await axios.put(`/api/degrees/${quickModal.degree.degree_id}`, {
                        degree_name: value,
                        adminId: currentUser.user_id
                    });
                }
            } else if (quickModal.type === 'major') {
                if (quickModal.action === 'add') {
                    await axios.post(`/api/departments/${quickModal.dept.dept_id}/majors`, {
                        degree_id: quickModal.degree.degree_id,
                        major_name: value,
                        adminId: currentUser.user_id
                    });
                } else if (quickModal.action === 'edit') {
                    if (value === quickModal.major.major_name) {
                        setQuickModal(null);
                        return;
                    }
                    await axios.put(`/api/majors/${quickModal.major.major_id}`, {
                        major_name: value,
                        adminId: currentUser.user_id
                    });
                }
            }
            setQuickModal(null);
            const updated = await refreshDepartmentState();
            if (quickModal.type === 'degree' && quickModal.action === 'add') {
                setFormData(prev => ({ ...prev, degree: value, major: '' }));
            }
            if (quickModal.type === 'degree' && quickModal.action === 'edit') {
                setFormData(prev => ({ ...prev, degree: value, major: '' }));
            }
            if (quickModal.type === 'major' && (quickModal.action === 'add' || quickModal.action === 'edit')) {
                setFormData(prev => ({ ...prev, major: value }));
            }
            setDepartments(updated);
        } catch (err) {
            alert(err.response?.data?.error || err.response?.data || `Failed to ${quickModal.action} ${quickModal.type}.`);
            console.error(err);
        }
    };

    const handleDeleteSelected = async (type) => {
        if (!isAdmin) return;
        const selectedDept = departments.find(d => d.dept_name === formData.department);
        const selectedDegree = degreesForDept.find(d => d.degree_name === formData.degree);
        const selectedMajor = majorsForDegree.find(m => m.major_name === formData.major);

        const target = type === 'department' ? selectedDept : type === 'degree' ? selectedDegree : selectedMajor;
        if (!target) {
            alert(`Please select a ${type} first.`);
            return;
        }

        const id = target.dept_id || target.degree_id || target.major_id;
        const name = target.dept_name || target.degree_name || target.major_name;
        if (!window.confirm(`Delete the "${name}" ${type}?`)) return;

        try {
            if (type === 'department') {
                await axios.delete(`/api/departments/${id}`, { params: { adminId: currentUser.user_id } });
                setFormData(prev => ({ ...prev, department: '', degree: '', major: '' }));
            } else {
                const url = type === 'degree' ? `/api/degrees/${id}` : `/api/majors/${id}`;
                await axios.delete(url, { params: { adminId: currentUser.user_id } });
                setFormData(prev => ({ ...prev, [type]: '', ...(type === 'degree' ? { major: '' } : {}) }));
            }
            await refreshDepartmentState('');
        } catch (err) {
            if (err.response?.status === 409) {
                createResolutionModal('delete-item', [{
                    type,
                    id,
                    name,
                    thesisCount: err.response.data?.thesisCount || 0
                }]);
                return;
            }
            alert(err.response?.data?.error || `Failed to delete ${type}.`);
            console.error(err);
        }
    };

    const submitDeleteResolution = async () => {
        const payloads = buildResolutionPayloads();
        if (!validateResolutionPayloads(payloads)) return;
        try {
            for (const payload of payloads) {
                if (payload.type === 'department') {
                    await axios.delete(`/api/departments/${payload.id}`, {
                        params: { adminId: currentUser.user_id, ...payload }
                    });
                    setFormData(prev => ({ ...prev, department: '', degree: '', major: '' }));
                } else {
                    const url = payload.type === 'degree' ? `/api/degrees/${payload.id}` : `/api/majors/${payload.id}`;
                    await axios.delete(url, { params: { adminId: currentUser.user_id, ...payload } });
                    setFormData(prev => ({
                        ...prev,
                        ...(payload.type === 'degree' ? { degree: '', major: '' } : { major: '' })
                    }));
                }
            }
            setResolutionModal(null);
            await refreshDepartmentState('');
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to complete delete action.');
            console.error(err);
        }
    };

    const validateAndShowConfirmation = async (e) => {
        e.preventDefault();
        
        if (formData.abstract.length < 20) {
            alert("Abstract must be at least 20 characters long.");
            return;
        }

        // Check if any author emails exist
        const authorEmails = authors.map(a => (a.email || '').trim()).filter(e => e);
        
        if (authorEmails.length === 0) {
            // No emails, proceed directly to upload
            handleSubmit();
            return;
        }

        // Validate emails
        setLoadingMessage('Validating author emails...');
        setIsLoading(true);
        const authorStrings = authors
            .filter(a => (a.firstName && a.firstName.trim() !== '') || (a.lastName && a.lastName.trim() !== ''))
            .map(a => {
                const first = (a.firstName || '').trim().replace(/[|,;]/g, '').toUpperCase();
                const last = (a.lastName || '').trim().replace(/[|,;]/g, '').toUpperCase();
                return `${first}||${last}`.trim();
            });

        try {
            const response = await axios.post('/api/validate-author-emails', {
                authors: authorStrings,
                emails: authorEmails
            });
            
            setValidationResults(response.data);
            setShowConfirmation(true);
        } catch (error) {
            alert("Error validating emails. Please try again.");
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        
        if (formData.abstract.length < 20) {
            alert("Abstract must be at least 20 characters long.");
            return;
        }

        // 2. THIS IS THE "DATA" OBJECT YOU SAW EARLIER
        const data = new FormData();
        data.append('title', formData.title.trim().toUpperCase());
        data.append('abstract', formData.abstract);
        data.append('year', formData.year);
        data.append('department', formData.department);
        if (file) {
            data.append('thesisFile', file);
        }
        
        // 3. ADD THE USER ID SO THE LOG KNOWS WHO UPLOADED IT
        data.append('userId', currentUser.user_id);
        
        // Add degree and major IDs if available
        if (formData.degree) {
            const dept = departments.find(d => d.dept_name === formData.department);
            const degreeObj = dept?.degrees?.find(d => d.degree_name === formData.degree);
            if (degreeObj) {
                data.append('degreeId', degreeObj.degree_id);
                if (formData.major) {
                    const majorObj = degreeObj.majors?.find(m => m.major_name === formData.major);
                    if (majorObj) {
                        data.append('majorId', majorObj.major_id);
                    }
                }
            }
        }

        // authors: send both structured details and normalized delimiter format
        const authorDetails = authors
            .filter(a => (a.firstName && a.firstName.trim() !== '') || (a.lastName && a.lastName.trim() !== ''))
            .map(a => {
                const first = (a.firstName || '').trim().replace(/[|,;]/g, '').toUpperCase();
                const last = (a.lastName || '').trim().replace(/[|,;]/g, '').toUpperCase();
                return { firstName: first, lastName: last };
            });
        const authorStrings = authorDetails.map(a => `${a.firstName}||${a.lastName}`.trim());
        const authorEmails = authors.map(a => (a.email || '').trim());
        data.append('authors', JSON.stringify(authorStrings));
        data.append('authorDetails', JSON.stringify(authorDetails));
        data.append('authorEmails', JSON.stringify(authorEmails));
        data.append('keywords', JSON.stringify(keywords.map(k => k.trim()).filter(k => k !== '')));

        setIsLoading(true);
        setLoadingMessage('Uploading thesis...');
        try {
            await axios.post('/api/theses', data);
            alert("Upload Successful!");
            setFormData({ title: '', abstract: '', year: '', department: '', degree: '', major: '' });
            setFile(null);
            setAuthors([{ firstName: '', lastName: '', email: '' }]);
            setKeywords([]);
            setDegreesForDept([]);
            setMajorsForDegree([]);
            setShowConfirmation(false);
        } catch (error) {
            alert(error.response?.data || "Upload failed");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="page page-narrow">
            {/* LOADING OVERLAY */}
            {isLoading && (
                <div className="loading-overlay">
                    <div className="loading-card">
                        <div className="spinner"></div>
                        <p>{loadingMessage}</p>
                    </div>
                </div>
            )}

            <div className="card">
                <h2 className="title">Upload New Thesis</h2>
                <form onSubmit={validateAndShowConfirmation} className="stack">
                <div>
                    <label className="label">Thesis Title</label>
                    <input type="text" name="title" onChange={handleChange} value={formData.title} required className="input" />
                </div>
                
                <div>
                    <label className="label">Abstract</label>
                    <textarea name="abstract" onChange={handleChange} value={formData.abstract} required className="textarea" />
                </div>
                
                <div>
                    <label className="label">Year</label>
                    <input type="number" name="year" onChange={handleChange} value={formData.year} required className="input" />
                </div>

                <div>
                    <div style={fieldHeaderStyle}>
                        <label className="label" style={{ margin: 0 }}>Department</label>
                        <ManagementButtons
                            disabled={!isAdmin}
                            onAdd={() => openQuickModal('department', 'add')}
                            onEdit={() => openQuickModal('department', 'edit')}
                            onDelete={() => handleDeleteSelected('department')}
                        />
                    </div>
                    <select
                        name="department"
                        onChange={handleChange}
                        value={formData.department}
                        required
                        className="select"
                        disabled={departments.length === 0}
                    >
                        <option value="" disabled>{departments.length === 0 ? 'No departments available' : 'Select Department'}</option>
                        {departments.map((dept) => (
                            <option key={dept.dept_id} value={dept.dept_name}>{dept.dept_name}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <div style={fieldHeaderStyle}>
                        <label className="label" style={{ margin: 0 }}>Degree</label>
                        <ManagementButtons
                            disabled={!isAdmin}
                            onAdd={() => openQuickModal('degree', 'add')}
                            onEdit={() => openQuickModal('degree', 'edit')}
                            onDelete={() => handleDeleteSelected('degree')}
                        />
                    </div>
                    <select
                        name="degree"
                        onChange={handleChange}
                        value={formData.degree}
                        className="select"
                        disabled={!formData.department || degreesForDept.length === 0}
                    >
                        <option value="" disabled>
                            {!formData.department ? 'Select Department first' : degreesForDept.length === 0 ? 'No degrees available' : 'Select Degree'}
                        </option>
                        {degreesForDept.map((degree) => (
                            <option key={degree.degree_id} value={degree.degree_name}>{degree.degree_name}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <div style={fieldHeaderStyle}>
                        <label className="label" style={{ margin: 0 }}>Major</label>
                        <ManagementButtons
                            disabled={!isAdmin}
                            onAdd={() => openQuickModal('major', 'add')}
                            onEdit={() => openQuickModal('major', 'edit')}
                            onDelete={() => handleDeleteSelected('major')}
                        />
                    </div>
                    <select
                        name="major"
                        onChange={handleChange}
                        value={formData.major}
                        className="select"
                        disabled={!formData.degree || majorsForDegree.length === 0}
                    >
                        <option value="" disabled>
                            {!formData.degree ? 'Select Degree first' : majorsForDegree.length === 0 ? 'No majors available' : 'Select Major'}
                        </option>
                        {majorsForDegree.map((major) => (
                            <option key={major.major_id} value={major.major_name}>{major.major_name}</option>
                        ))}
                    </select>
                </div>

                {!isAdmin && (
                    <p className="notice notice-info" style={{ margin: 0 }}>
                        Only admins can add, edit, or delete departments, degrees, and majors.
                    </p>
                )}

                <div>
                    <label className="label">PDF File (Optional)</label>
                    <input type="file" accept="application/pdf" onChange={handleFileChange} className="input" />
                </div>
                <div>
                    <label className="label">Authors</label>
                    <p className="muted" style={{ marginBottom: '10px', fontStyle: 'italic' }}>
                        <strong>Note:</strong> Author email addresses are stored only for guest PDF requests, so thesis authors can respond directly by email.
                    </p>
                    {authors.map((a, idx) => (
                        <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                                <input
                                    type="text"
                                    placeholder="First Name"
                                    value={a.firstName}
                                    onChange={(e) => {
                                        const copy = [...authors]; copy[idx].firstName = e.target.value.toUpperCase(); setAuthors(copy);
                                    }}
                                    className="input"
                                    style={{ marginBottom: '4px' }}
                                />
                                <input
                                    type="text"
                                    placeholder="Last Name"
                                    value={a.lastName}
                                    onChange={(e) => {
                                        const copy = [...authors]; copy[idx].lastName = e.target.value.toUpperCase(); setAuthors(copy);
                                    }}
                                    className="input"
                                    style={{ marginBottom: '4px' }}
                                />
                                <input
                                    type="email"
                                    placeholder="Email (Optional)"
                                    value={a.email}
                                    onChange={(e) => {
                                        const copy = [...authors]; copy[idx].email = e.target.value; setAuthors(copy);
                                    }}
                                    className="input"
                                />
                            </div>
                            <button type="button" onClick={() => {
                                const copy = [...authors]; copy.splice(idx, 1); setAuthors(copy.length ? copy : [{ firstName: '', lastName: '', email: '' }]);
                            }} className="btn btn-outline btn-small" style={{ height: 'fit-content', marginTop: '4px' }}>Remove</button>
                        </div>
                    ))}
                    <button type="button" onClick={() => setAuthors([...authors, { firstName: '', lastName: '', email: '' }])} className="btn btn-outline btn-small">Add Author</button>
                </div>

                <div>
                    <label className="label">Keywords (tags)</label>
                    <KeywordInput keywords={keywords} setKeywords={setKeywords} />
                </div>
                
                <button type="submit" className="btn btn-primary">
                    Upload Thesis
                </button>
            </form>
            </div>

            {showDeptModal && (
                <div className="modal-overlay" onClick={closeDeptModal}>
                    <div className="modal" style={{ maxWidth: '620px' }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ marginTop: 0 }}>{isEditingDept ? 'Edit Department' : 'Add New Department'}</h3>

                        <div className="modal-body" style={{ maxHeight: '520px' }}>
                            <div style={{ marginBottom: '15px' }}>
                                <label className="label">Department Name</label>
                                <input
                                    type="text"
                                    value={deptFormData.name}
                                    onChange={(e) => setDeptFormData({ ...deptFormData, name: e.target.value })}
                                    placeholder="e.g., Computer Science"
                                    className="input"
                                />
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <label className="label">Description (Optional)</label>
                                <textarea
                                    value={deptFormData.description}
                                    onChange={(e) => setDeptFormData({ ...deptFormData, description: e.target.value })}
                                    placeholder="e.g., Department focused on computer science and technology"
                                    className="textarea"
                                    style={{ height: '100px' }}
                                />
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <label className="label">Degrees (Optional)</label>
                                <div className="row" style={{ marginBottom: '10px' }}>
                                    <input
                                        type="text"
                                        value={newDegreeInput}
                                        onChange={(e) => setNewDegreeInput(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddDegreeToForm(); } }}
                                        placeholder="Add a degree"
                                        className="input"
                                    />
                                    <button type="button" onClick={handleAddDegreeToForm} className="btn btn-success btn-small">Add</button>
                                </div>

                                {deptDegrees.length > 0 ? (
                                    <div style={nestedListStyle}>
                                        {deptDegrees.map((degree, degreeIdx) => (
                                            <div key={degree.degree_id || degreeIdx} style={nestedItemStyle}>
                                                <div style={nestedRowStyle}>
                                                    {inlineDegreeEdit.index === degreeIdx ? (
                                                        <input
                                                            type="text"
                                                            value={inlineDegreeEdit.value}
                                                            onChange={(e) => setInlineDegreeEdit({ index: degreeIdx, value: e.target.value })}
                                                            className="input"
                                                            style={{ flex: 1 }}
                                                        />
                                                    ) : (
                                                        <strong>{degree.degree_name}</strong>
                                                    )}
                                                    <div style={buttonGroupStyle}>
                                                        {inlineDegreeEdit.index === degreeIdx ? (
                                                            <>
                                                                <button type="button" onClick={() => saveInlineDegreeEdit(degreeIdx)} className="btn btn-ghost btn-small">Save</button>
                                                                <button type="button" onClick={() => setInlineDegreeEdit({ index: null, value: '' })} className="btn btn-ghost btn-small">Cancel</button>
                                                            </>
                                                        ) : (
                                                            <button type="button" onClick={() => setInlineDegreeEdit({ index: degreeIdx, value: degree.degree_name })} className="btn btn-ghost btn-small">Edit</button>
                                                        )}
                                                        <button type="button" onClick={() => handleRemoveDegreeFromForm(degreeIdx)} className="btn btn-ghost btn-small text-danger">Remove</button>
                                                    </div>
                                                </div>

                                                <div className="row" style={{ margin: '8px 0' }}>
                                                    <input
                                                        type="text"
                                                        value={newMajorInputs[degreeIdx] || ''}
                                                        onChange={(e) => setNewMajorInputs(prev => ({ ...prev, [degreeIdx]: e.target.value }))}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddMajorToDegree(degreeIdx); } }}
                                                        placeholder="Add a major for this degree"
                                                        className="input"
                                                    />
                                                    <button type="button" onClick={() => handleAddMajorToDegree(degreeIdx)} className="btn btn-success btn-small">Add</button>
                                                </div>

                                                {(degree.majors || []).length > 0 && (
                                                    <div style={majorListStyle}>
                                                        {degree.majors.map((major, majorIdx) => (
                                                            <div key={major.major_id || majorIdx} style={nestedRowStyle}>
                                                                {inlineMajorEdit.degreeIndex === degreeIdx && inlineMajorEdit.majorIndex === majorIdx ? (
                                                                    <input
                                                                        type="text"
                                                                        value={inlineMajorEdit.value}
                                                                        onChange={(e) => setInlineMajorEdit({ degreeIndex: degreeIdx, majorIndex: majorIdx, value: e.target.value })}
                                                                        className="input"
                                                                        style={{ flex: 1 }}
                                                                    />
                                                                ) : (
                                                                    <span>{major.major_name}</span>
                                                                )}
                                                                <div style={buttonGroupStyle}>
                                                                    {inlineMajorEdit.degreeIndex === degreeIdx && inlineMajorEdit.majorIndex === majorIdx ? (
                                                                        <>
                                                                            <button type="button" onClick={() => saveInlineMajorEdit(degreeIdx, majorIdx)} className="btn btn-ghost btn-small">Save</button>
                                                                            <button type="button" onClick={() => setInlineMajorEdit({ degreeIndex: null, majorIndex: null, value: '' })} className="btn btn-ghost btn-small">Cancel</button>
                                                                        </>
                                                                    ) : (
                                                                        <button type="button" onClick={() => setInlineMajorEdit({ degreeIndex: degreeIdx, majorIndex: majorIdx, value: major.major_name })} className="btn btn-ghost btn-small">Edit</button>
                                                                    )}
                                                                    <button type="button" onClick={() => handleRemoveMajorFromDegree(degreeIdx, majorIdx)} className="btn btn-ghost btn-small text-danger">Remove</button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="muted" style={{ margin: 0 }}>No degrees added.</p>
                                )}
                            </div>
                        </div>

                        <div className="row" style={{ marginTop: '16px' }}>
                            <button type="button" onClick={() => handleSaveDepartment()} className="btn btn-primary" style={{ flex: 1 }}>
                                {isEditingDept ? 'Update Department' : 'Save Department'}
                            </button>
                            <button type="button" onClick={closeDeptModal} className="btn btn-outline" style={{ flex: 1 }}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {quickModal && (
                <div className="modal-overlay" onClick={() => setQuickModal(null)}>
                    <div className="modal" style={{ maxWidth: '520px' }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ marginTop: 0 }}>
                            {quickModal.action === 'add' ? 'Add' : 'Edit'} {quickModal.type === 'degree' ? 'Degree' : 'Major'}
                        </h3>
                        <p className="subtitle" style={{ marginTop: 0 }}>
                            {quickModal.type === 'degree'
                                ? `Department: ${quickModal.dept?.dept_name || ''}`
                                : `Degree: ${quickModal.degree?.degree_name || ''}`}
                        </p>
                        <input
                            type="text"
                            className="input"
                            value={quickModal.value}
                            onChange={(e) => setQuickModal(prev => ({ ...prev, value: e.target.value }))}
                            placeholder={`Enter ${quickModal.type} name`}
                            style={{ marginBottom: '16px' }}
                        />
                        <div className="row">
                            <button type="button" onClick={submitQuickModal} className="btn btn-primary" style={{ flex: 1 }}>Save</button>
                            <button type="button" onClick={() => setQuickModal(null)} className="btn btn-outline" style={{ flex: 1 }}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {resolutionModal && (
                <div className="modal-overlay" onClick={() => setResolutionModal(null)}>
                    <div className="modal" style={{ maxWidth: '680px' }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ marginTop: 0 }}>Resolve Assigned Theses</h3>
                        <p className="subtitle" style={{ marginTop: 0 }}>
                            Choose whether to delete the assigned theses or move them to a specific department, degree, and major.
                        </p>
                        <div className="modal-body" style={{ maxHeight: '420px' }}>
                            {resolutionModal.conflicts.map((conflict) => {
                                const key = conflictKey(conflict);
                                const selection = resolutionModal.selections[key] || {};
                                const targetDepartments = departments.filter(d => !(conflict.type === 'department' && String(d.dept_id) === String(conflict.id)));
                                const targetDegrees = getDegreesForResolution(selection).filter(d => !(conflict.type === 'degree' && String(d.degree_id) === String(conflict.id)));
                                const targetMajors = getMajorsForResolution(selection).filter(m => !(conflict.type === 'major' && String(m.major_id) === String(conflict.id)));
                                return (
                                    <div key={key} style={resolutionBoxStyle}>
                                        <strong>{conflict.type.charAt(0).toUpperCase() + conflict.type.slice(1)}: {conflict.name}</strong>
                                        <p className="muted" style={{ margin: '6px 0 10px' }}>Assigned to {conflict.thesisCount} thesis project(s).</p>
                                        <select
                                            className="select"
                                            value={selection.action || 'delete_theses'}
                                            onChange={(e) => updateResolutionSelection(conflict, { action: e.target.value })}
                                            style={{ marginBottom: '10px' }}
                                        >
                                            <option value="delete_theses">Delete all assigned theses</option>
                                            <option value="move_to">Move assigned theses to selected options</option>
                                            {conflict.type !== 'department' && <option value="keep_null">Keep theses without this {conflict.type}</option>}
                                        </select>

                                        {selection.action === 'move_to' && (
                                            <>
                                                <label className="label">Department</label>
                                                <select
                                                    className="select"
                                                    value={selection.targetDeptId || ''}
                                                    onChange={(e) => updateResolutionSelection(conflict, { targetDeptId: e.target.value })}
                                                >
                                                    <option value="">Select Department</option>
                                                    {targetDepartments.map(dept => <option key={dept.dept_id} value={dept.dept_id}>{dept.dept_name}</option>)}
                                                </select>

                                                <label className="label" style={{ marginTop: '10px' }}>Degree</label>
                                                <select
                                                    className="select"
                                                    value={selection.targetDegreeId || ''}
                                                    onChange={(e) => updateResolutionSelection(conflict, { targetDegreeId: e.target.value })}
                                                    disabled={!selection.targetDeptId || getDegreesForResolution(selection).length === 0}
                                                >
                                                    <option value="">{!selection.targetDeptId ? 'Select Department first' : targetDegrees.length === 0 ? 'No degrees available' : 'Select Degree'}</option>
                                                    {targetDegrees.map(degree => <option key={degree.degree_id} value={degree.degree_id}>{degree.degree_name}</option>)}
                                                </select>

                                                <label className="label" style={{ marginTop: '10px' }}>Major</label>
                                                <select
                                                    className="select"
                                                    value={selection.targetMajorId || ''}
                                                    onChange={(e) => updateResolutionSelection(conflict, { targetMajorId: e.target.value })}
                                                    disabled={!selection.targetDegreeId || getMajorsForResolution(selection).length === 0}
                                                >
                                                    <option value="">{!selection.targetDegreeId ? 'Select Degree first' : targetMajors.length === 0 ? 'No majors available' : 'Select Major'}</option>
                                                    {targetMajors.map(major => <option key={major.major_id} value={major.major_id}>{major.major_name}</option>)}
                                                </select>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="row" style={{ marginTop: '14px' }}>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={resolutionModal.mode === 'save-department' ? submitDepartmentSaveResolution : submitDeleteResolution}
                                style={{ flex: 1 }}
                            >
                                Apply Resolution
                            </button>
                            <button type="button" className="btn btn-outline" onClick={() => setResolutionModal(null)} style={{ flex: 1 }}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* CONFIRMATION MODAL FOR AUTHOR ACCOUNTS */}
            {showConfirmation && validationResults && (
                <div className="modal-overlay" onClick={() => setShowConfirmation(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ marginTop: 0 }}>Confirm Author Email Details</h3>
                        
                        <div className="modal-body" style={{ maxHeight: '400px' }}>
                            <p className="subtitle" style={{ marginBottom: '15px' }}>
                                The following author email details will be saved:
                            </p>

                            {validationResults.map((result, idx) => (
                                <div key={idx} className={resultBoxClass(result.status)}>
                                    <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{result.name}</p>
                                    <p style={{ margin: '0 0 5px 0', fontSize: '0.85rem', color: '#666' }}>
                                        Email: {result.email}
                                    </p>
                                    <p style={{ margin: 0, fontSize: '0.85rem', fontStyle: 'italic' }}>
                                        {result.status === 'new' && 'Email will be saved for author contact requests'}
                                        {result.status === 'existing' && `Email already belongs to author: ${result.existingName}`}
                                        {result.status === 'no_email' && '- No email provided'}
                                    </p>
                                </div>
                            ))}
                        </div>
                        <p className="notice notice-info" style={{ margin: '15px 0' }}>
                            <strong>Note:</strong> No user account will be created. Guest PDF requests will simply email the listed authors directly.
                        </p>
                        <div className="row">
                            <button 
                                onClick={handleSubmit}
                                className="btn btn-success"
                                style={{ flex: 1 }}
                            >
                                Confirm & Upload
                            </button>
                            <button 
                                onClick={() => setShowConfirmation(false)}
                                className="btn btn-outline"
                                style={{ flex: 1 }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const fieldHeaderStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' };
const buttonGroupStyle = { display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' };
const nestedListStyle = { background: '#f9f9f9', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px', maxHeight: '360px', overflowY: 'auto' };
const nestedItemStyle = { padding: '10px 0', borderBottom: '1px solid #eee' };
const nestedRowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '5px 0' };
const majorListStyle = { background: '#fff', border: '1px solid #eee', borderRadius: '4px', padding: '6px 10px' };
const resolutionBoxStyle = { border: '1px solid #e5e7eb', borderRadius: '6px', padding: '12px', marginBottom: '12px', background: '#fafafa' };

const ManagementButtons = ({ disabled, onAdd, onEdit, onDelete }) => (
    <div style={buttonGroupStyle}>
        <button type="button" onClick={onAdd} disabled={disabled} className="btn btn-outline btn-small" style={disabled ? disabledManageBtnStyle : undefined}>Add</button>
        <button type="button" onClick={onEdit} disabled={disabled} className="btn btn-outline btn-small" style={disabled ? disabledManageBtnStyle : undefined}>Edit</button>
        <button type="button" onClick={onDelete} disabled={disabled} className="btn btn-outline btn-small" style={disabled ? disabledManageBtnStyle : undefined}>Delete</button>
    </div>
);

const disabledManageBtnStyle = { opacity: 0.45, cursor: 'not-allowed' };

const resultBoxClass = (status) => {
    const base = 'result-box';
    switch(status) {
        case 'new': return `${base} result-new`;
        case 'existing': return `${base} result-existing`;
        case 'no_email': return `${base} result-none`;
        default: return base;
    }
};

const KeywordInput = ({ keywords, setKeywords }) => {
    const [input, setInput] = useState('');

    const add = () => {
        const val = input.trim();
        if (!val) return;
        if (!keywords.includes(val)) setKeywords([...keywords, val]);
        setInput('');
    };

    return (
        <div>
            <div className="row">
                <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} placeholder="Add keyword and press Enter" className="input" />
                <button type="button" onClick={add} className="btn btn-outline btn-small">Add</button>
            </div>
            <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {keywords.map((k, i) => (
                    <div key={i} className="chip">
                        <span style={{ fontSize: '13px' }}>{k}</span>
                        <button type="button" onClick={() => setKeywords(keywords.filter((_, idx) => idx !== i))} className="btn btn-ghost btn-small">✕</button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ThesisUpload;



